/**
 * MarketPositionerSkeleton
 *
 * A skeleton loading state for the MarketPositioner component
 */

import React from 'react'
import { cn } from '@/lib/utils'

interface MarketPositionerSkeletonProps {
  className?: string
}

export const MarketPositionerSkeleton: React.FC<MarketPositionerSkeletonProps> = ({
  className
}) => {
  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex animate-pulse flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <div className="size-4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-36 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>

      {/* Market positioning chart */}
      <div className="p-4">
        <div className="flex h-[300px] w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
          <div className="relative size-full">
            {/* Axis lines */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-full w-px bg-gray-300 dark:bg-gray-600"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-px w-full bg-gray-300 dark:bg-gray-600"></div>
            </div>

            {/* Axis labels */}
            <div className="absolute left-1/2 top-2 -translate-x-1/2">
              <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
              <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div className="absolute left-2 top-1/2 -translate-y-1/2">
              <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>

            {/* Company markers */}
            <div className="absolute left-1/3 top-1/4 -translate-x-1/2 -translate-y-1/2">
              <div className="size-10 rounded-full bg-blue-200 dark:bg-blue-900"></div>
            </div>
            <div className="absolute left-2/3 top-2/3 -translate-x-1/2 -translate-y-1/2">
              <div className="size-8 rounded-full bg-green-200 dark:bg-green-900"></div>
            </div>
            <div className="absolute left-3/4 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="size-9 rounded-full bg-purple-200 dark:bg-purple-900"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Company list */}
      <div className="border-t p-4">
        <div className="space-y-3">
          {/* Company items */}
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className={`size-6 rounded-full ${
                  i === 0
                    ? 'bg-blue-200 dark:bg-blue-900'
                    : i === 1
                      ? 'bg-green-200 dark:bg-green-900'
                      : 'bg-purple-200 dark:bg-purple-900'
                }`}
              ></div>
              <div className="flex-1 space-y-1">
                <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-3 w-8 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-8 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
